/**
 * DebugPanel - Side panel for admin users to view debug data
 *
 * Shows system prompt, messages sent to LLM, and LLM response
 * from the most recent chat message.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import './DebugPanel.css';

const DEFAULT_WIDTH = 450;
const MIN_WIDTH = 380;
const MAX_WIDTH = 600;

// Copies `text` to the clipboard. Tries the modern async API first; falls
// back to the execCommand('copy') hidden-textarea trick for non-secure/
// non-HTTPS contexts where navigator.clipboard isn't available. Returns
// whether the copy succeeded — never throws (mirrors the Corridor's silent-
// on-failure pattern used elsewhere in the frontend, e.g. SceneInspector's
// copyBrief).
async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (_) {
      // fall through to the legacy fallback below
    }
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);
    return success;
  } catch (_) {
    return false;
  }
}

// Renders a message array as a plain-text transcript, one block per message,
// each prefixed with its role — the same shape MessageDisplay renders
// visually, flattened for copy/paste instead of raw JSON.
function formatMessagesAsText(messages) {
  if (!messages || messages.length === 0) return '';
  return messages
    .map((msg) => `${(msg.role || '').toUpperCase()}:\n${msg.content || ''}`)
    .join('\n\n');
}

function CollapsibleSection({ title, defaultOpen = false, children, charCount, onCopy }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);

  const copyText = onCopy ? onCopy() : null;
  const hasCopyableText = !!copyText;

  const toggleOpen = () => setIsOpen((prev) => !prev);

  const handleHeaderKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleOpen();
    }
  };

  const handleCopyClick = async (e) => {
    e.stopPropagation();
    if (!hasCopyableText) return;
    const success = await copyToClipboard(copyText);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className="debug-panel__section">
      <div
        className="debug-panel__section-header"
        role="button"
        tabIndex={0}
        onClick={toggleOpen}
        onKeyDown={handleHeaderKeyDown}
      >
        <span className="debug-panel__section-title">{title}</span>
        <span className="debug-panel__section-meta">
          {charCount !== undefined && (
            <span className="debug-panel__char-count">{charCount.toLocaleString()} chars</span>
          )}
          {onCopy && (
            <button
              type="button"
              className="debug-panel__copy-btn"
              onClick={handleCopyClick}
              disabled={!hasCopyableText}
              title={hasCopyableText ? 'Copy to clipboard' : 'Nothing to copy'}
            >
              {copied ? 'Copied ✓' : 'Copy'}
            </button>
          )}
          <span className="debug-panel__section-toggle">{isOpen ? '▼' : '▶'}</span>
        </span>
      </div>
      {isOpen && (
        <div className="debug-panel__section-content">
          {children}
        </div>
      )}
    </div>
  );
}

function MessageDisplay({ messages }) {
  if (!messages || messages.length === 0) {
    return <p className="debug-panel__empty">No messages</p>;
  }

  return (
    <div className="debug-panel__messages">
      {messages.map((msg, idx) => (
        <div key={idx} className={`debug-panel__message debug-panel__message--${msg.role}`}>
          <div className="debug-panel__message-header">
            <span className="debug-panel__message-role">{msg.role}</span>
            <span className="debug-panel__message-length">{msg.content?.length || 0} chars</span>
          </div>
          <pre className="debug-panel__message-content">{msg.content}</pre>
        </div>
      ))}
    </div>
  );
}

export default function DebugPanel({ onClose, width, onWidthChange }) {
  const { lastDebugData } = useApp();
  const [isResizing, setIsResizing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Resize handlers
  const handleResizeStart = useCallback((e) => {
    if (isExpanded) return; // Don't resize when expanded
    e.preventDefault();
    setIsResizing(true);
  }, [isExpanded]);

  const toggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const newWidth = window.innerWidth - e.clientX;
      const clamped = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
      onWidthChange?.(clamped);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, onWidthChange]);

  const debugData = lastDebugData || {};
  const {
    session_id,
    template,
    system_prompt,
    recent_messages,
    llm_messages,
    llm_response,
    token_estimate,
    context_summary
  } = debugData;

  return (
    <div
      className={`debug-panel ${isExpanded ? 'debug-panel--expanded' : ''}`}
      style={{ width: isExpanded ? '100%' : (width || DEFAULT_WIDTH) }}
    >
      {/* Resize handle - hidden when expanded */}
      {!isExpanded && (
        <div
          className={`debug-panel__resize-handle ${isResizing ? 'debug-panel__resize-handle--active' : ''}`}
          onMouseDown={handleResizeStart}
        />
      )}

      {/* Header */}
      <div className="debug-panel__header">
        <div className="debug-panel__title">
          <span className="debug-panel__icon">🔧</span>
          <span>Debug Panel</span>
          {token_estimate && (
            <span className="debug-panel__tokens">~{token_estimate.toLocaleString()} tokens</span>
          )}
        </div>
        <div className="debug-panel__header-actions">
          <button
            className="debug-panel__expand-btn"
            onClick={toggleExpand}
            title={isExpanded ? "Collapse panel" : "Expand to full width"}
          >
            {isExpanded ? '◀▶' : '◀▶'}
          </button>
          <button
            className="debug-panel__close-btn"
            onClick={onClose}
            title="Close Debug Panel"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="debug-panel__content">
        {!lastDebugData ? (
          <div className="debug-panel__empty-state">
            <p>No debug data available.</p>
            <p className="debug-panel__hint">Send a message in the chat to see debug information here.</p>
          </div>
        ) : (
          <>
            {/* Debug info header showing session and template */}
            <div className="debug-panel__info-header">
              <div className="debug-panel__info-row">
                <span className="debug-panel__info-label">Session:</span>
                <span className="debug-panel__info-value">{session_id?.slice(-8) || 'Unknown'}</span>
              </div>
              <div className="debug-panel__info-row">
                <span className="debug-panel__info-label">Template:</span>
                <span className="debug-panel__info-value debug-panel__template-badge">{template || 'open'}</span>
              </div>
            </div>

            <CollapsibleSection
              title="System Prompt"
              defaultOpen={true}
              charCount={system_prompt?.length || 0}
              onCopy={() => system_prompt || ''}
            >
              <pre className="debug-panel__text">{system_prompt || 'Not available'}</pre>
            </CollapsibleSection>

            <CollapsibleSection
              title="Context Summary"
              charCount={context_summary?.length || 0}
              onCopy={() => context_summary || ''}
            >
              <pre className="debug-panel__text">{context_summary || 'No context summary'}</pre>
            </CollapsibleSection>

            <CollapsibleSection
              title="Recent Messages in Context"
              onCopy={() => formatMessagesAsText(recent_messages)}
            >
              <MessageDisplay messages={recent_messages} />
            </CollapsibleSection>

            <CollapsibleSection
              title="Messages Sent to LLM"
              defaultOpen={true}
              onCopy={() => formatMessagesAsText(llm_messages)}
            >
              <MessageDisplay messages={llm_messages} />
            </CollapsibleSection>

            <CollapsibleSection
              title="LLM Response"
              charCount={llm_response?.length || 0}
              onCopy={() => llm_response || ''}
            >
              <pre className="debug-panel__text">{llm_response || 'No response'}</pre>
            </CollapsibleSection>
          </>
        )}
      </div>
    </div>
  );
}
